import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import PageNav from '../components/PageNav'

import { ApiError, apiFetch, fetchActivityPdf } from '../lib/api'

// Activity report — the management report over `cvStudyFacilityReport`.
//
// Behavioural specification: `ActivityReportPDF.aspx` and `ActivityReportViewer.aspx`.
// The legacy renders `Reports/ActivityReport.rpt` in a Crystal viewer, driven by
// a `RecordSelectionFormula` built from nine session values. This is the data
// half: the same filters, the same view, plus the grouped totals the template
// subtotals.
//
// Print produces the document the legacy page actually handed the user. Its
// layout is not `ActivityReport.rpt`'s — that template is one of the two whose
// streams are encrypted, so there is nothing to bind — but the rows, filters and
// totals are the same. See `app/domain/activity/printable.py`.
//
// Filter state lives in the URL, as on the worklist, so a report can be
// bookmarked and shared — the legacy kept it in session, so it could not be.

interface Row {
  StudyDate: string | null
  PatientID: string | null
  PatientName: string | null
  PatientBirthDate: string | null
  PatientSex: string | null
  StudyType: string | null
  StudyStatus: string | null
  StudyFacility: string | null
  ReadingFacility: string | null
  ReadingPhysician: string | null
  ReferringPhysician: string | null
  Technician: string | null
}
interface Page {
  rows: Row[]
  total: number
  page: number
  page_size: number
  pages: number
}
interface Options {
  statuses: string[]
  study_types: string[]
  facilities: string[]
  group_by: string[]
}
interface Group {
  group: string | null
  studies: number
}

const COLUMNS: { key: keyof Row; label: string }[] = [
  { key: 'StudyDate', label: 'Study Date' },
  { key: 'PatientID', label: 'ID#' },
  { key: 'PatientName', label: 'Patient Name' },
  { key: 'StudyType', label: 'Study Type' },
  { key: 'StudyStatus', label: 'Status' },
  { key: 'StudyFacility', label: 'Study Facility' },
  { key: 'ReadingPhysician', label: 'Interpreting MD' },
  { key: 'ReferringPhysician', label: 'Referring Physician' },
  { key: 'Technician', label: 'Technician' },
]

export default function ActivityReportPage() {
  const [params, setParams] = useSearchParams()
  const [options, setOptions] = useState<Options | null>(null)
  const [data, setData] = useState<Page | null>(null)
  const [groups, setGroups] = useState<Group[] | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const get = (k: string, d = '') => params.get(k) ?? d
  const groupBy = get('by', 'StudyFacility')

  useEffect(() => {
    apiFetch<Options>('/api/v1/activity/options')
      .then(setOptions)
      .catch((e) => setError(e instanceof ApiError ? e.message : String(e)))
  }, [])

  const [printing, setPrinting] = useState(false)

  // Opened in a new tab rather than downloaded: the legacy framed the PDF in an
  // iframe, so a viewer is what the user expects, and the blob URL keeps the
  // session's bearer token out of a plain link.
  const print = useCallback(async () => {
    setPrinting(true)
    setError(null)
    try {
      const qs = new URLSearchParams(params)
      qs.delete('by')
      qs.delete('page')
      qs.set('by', groupBy)
      const { url } = await fetchActivityPdf('activity', qs.toString())
      window.open(url, '_blank', 'noopener')
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e))
    } finally {
      setPrinting(false)
    }
  }, [params, groupBy])

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    const qs = new URLSearchParams(params)
    qs.delete('by')
    if (!qs.has('pageSize')) qs.set('pageSize', '50')
    try {
      // Rows and totals are two queries over a 722k-row view; run them together
      // rather than serially.
      const [page, summary] = await Promise.all([
        apiFetch<Page>(`/api/v1/activity?${qs.toString()}`),
        apiFetch<{ groups: Group[] }>(`/api/v1/activity/summary?by=${groupBy}&${qs.toString()}`),
      ])
      setData(page)
      setGroups(summary.groups)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e))
    } finally {
      setLoading(false)
    }
  }, [params, groupBy])

  useEffect(() => {
    void load()
  }, [load])

  function update(patch: Record<string, string>, resetPage = true) {
    const next = new URLSearchParams(params)
    for (const [k, v] of Object.entries(patch)) {
      if (v === '') next.delete(k)
      else next.set(k, v)
    }
    if (resetPage) next.delete('page')
    setParams(next)
  }

  const totalShown = groups?.reduce((a, g) => a + g.studies, 0) ?? 0

  return (
    <main className="page page-wide">
      {/* `ActivityReportViewer.aspx:17` — "Home  AdminPage  Log Off", including
          the caption `AdminPage`, which is what the link says. */}
      <PageNav strip="activity" />
      <h1>Activity Report</h1>

      {error && <p className="banner banner-error">{error}</p>}

      <fieldset className="filters">
        <legend>Selection</legend>
        <div className="filter-row">
          <label>
            From
            <input
              type="date"
              value={get('beginDate')}
              onChange={(e) => update({ beginDate: e.target.value })}
            />
          </label>
          <label>
            To
            <input
              type="date"
              value={get('endDate')}
              onChange={(e) => update({ endDate: e.target.value })}
            />
          </label>
          <label>
            Study Facility
            <select
              value={get('studyFacility')}
              onChange={(e) => update({ studyFacility: e.target.value })}
            >
              <option value="">All</option>
              {options?.facilities.map((f) => (
                <option key={f} value={f}>
                  {f}
                </option>
              ))}
            </select>
          </label>
          <label>
            Study Type
            <select
              value={get('studyType')}
              onChange={(e) => update({ studyType: e.target.value })}
            >
              <option value="">All</option>
              {options?.study_types.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </label>
          <label>
            Status
            <select
              value={get('studyStatus')}
              onChange={(e) => update({ studyStatus: e.target.value })}
            >
              <option value="">All</option>
              {options?.statuses.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </label>
          <label>
            Patient Name
            <input
              defaultValue={get('patientName')}
              onBlur={(e) => update({ patientName: e.target.value })}
              onKeyDown={(e) =>
                e.key === 'Enter' && update({ patientName: e.currentTarget.value })
              }
            />
          </label>
          <button type="button" onClick={() => setParams(new URLSearchParams())}>
            Clear
          </button>
        </div>
      </fieldset>

      <div className="two-col">
        <section>
          <div className="grid-meta">
            <label className="inline-label">
              Totals by
              <select value={groupBy} onChange={(e) => update({ by: e.target.value }, false)}>
                {(options?.group_by ?? []).map((g) => (
                  <option key={g} value={g}>
                    {g}
                  </option>
                ))}
              </select>
            </label>
            {/* `ActivityReportPDF.aspx` — the document the legacy screen
                produced, which the grid alone never replaced. */}
            <button type="button" onClick={() => void print()} disabled={printing}>
              {printing ? 'Printing…' : 'Print'}
            </button>
          </div>
          <table className="grid">
            <thead>
              <tr>
                <th>{groupBy}</th>
                <th className="num">Studies</th>
              </tr>
            </thead>
            <tbody>
              {groups?.slice(0, 40).map((g) => (
                <tr key={String(g.group)}>
                  <td>{g.group || <span className="muted">(blank)</span>}</td>
                  <td className="num">{g.studies.toLocaleString()}</td>
                </tr>
              ))}
              {groups && groups.length > 0 && (
                <tr className="total-row">
                  <td>Total</td>
                  <td className="num">{totalShown.toLocaleString()}</td>
                </tr>
              )}
              {groups && groups.length === 0 && (
                <tr>
                  <td colSpan={2} className="muted">
                    No studies match.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          {groups && groups.length > 40 && (
            <p className="muted">Showing the 40 largest of {groups.length} groups.</p>
          )}
        </section>

        <section>
          <div className="grid-meta">
            {loading ? 'Loading…' : data ? `${data.total.toLocaleString()} studies` : ''}
            {data && data.pages > 1 && (
              <span className="muted">
                {' '}
                · page {data.page} of {data.pages.toLocaleString()}
              </span>
            )}
          </div>
          <div className="grid-scroll">
            <table className="grid">
              <thead>
                <tr>
                  {COLUMNS.map((c) => (
                    <th key={c.key}>{c.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data?.rows.map((r, i) => (
                  <tr key={`${r.PatientID}-${r.StudyDate}-${i}`}>
                    {COLUMNS.map((c) => (
                      <td key={c.key}>
                        {c.key === 'StudyDate' ? fmtDate(r[c.key]) : r[c.key]}
                      </td>
                    ))}
                  </tr>
                ))}
                {data && data.rows.length === 0 && !loading && (
                  <tr>
                    <td colSpan={COLUMNS.length} className="muted">
                      No studies match.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {data && data.pages > 1 && (
            <div className="pager">
              <button
                type="button"
                disabled={data.page <= 1}
                onClick={() => update({ page: String(data.page - 1) }, false)}
              >
                Previous
              </button>
              <span className="muted">
                {data.page} / {data.pages.toLocaleString()}
              </span>
              <button
                type="button"
                disabled={data.page >= data.pages}
                onClick={() => update({ page: String(data.page + 1) }, false)}
              >
                Next
              </button>
            </div>
          )}
        </section>
      </div>
    </main>
  )
}

function fmtDate(v: string | null) {
  return v ? v.slice(0, 10) : ''
}
